<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined :loading="isLoading">
            <v-container>
              <v-row class="mt-0">
                <v-col sm="4">
                  <select-list
                    v-model="query.params.offerType"
                    :items="offerTypes"
                    label="Offer Type"
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list v-model="query.params.offer" :items="offers" label="Offer" outlined hide-details />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.site"
                    :disabled="isLoading"
                    :items="mappedSites"
                    hide-details
                    outlined
                    label="Website"
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.countryCode"
                    :items="countries"
                    custom-type="country"
                    label="Country"
                    outlined
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.language"
                    :items="languages"
                    label="Language"
                    outlined
                    hide-details
                  />
                </v-col>
              </v-row>
            </v-container>
            <v-container>
              <v-row>
                <v-col>
                  <template v-if="canPreview && orderedTrafficPartners.length">
                    <traffic-partner-list
                      v-for="({ partners, priority }, groupIndex) in orderedTrafficPartners"
                      :key="priority"
                      :partners="partners"
                      :priority="priority"
                      :class="{ 'mt-4': groupIndex !== 0 }"
                    />
                  </template>
                  <v-list v-else dense class="text-center grey lighten-3">
                    <v-list-item>
                      <v-list-item-subtitle>
                        <span v-if="isLoading">Fetching data...</span>
                        <span v-else-if="canPreview"
                          >No partners found. Try searching a different offer or country!</span
                        >
                        <span v-else-if="!query.params.offer">Select an <strong>Offer</strong> to preview!</span>
                        <span v-else-if="!query.params.site">Please select a Website</span>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <generic-modal v-model="showHelpModal" title="Traffic Flow Explained" hide-buttons>
      <p>
        When a user attempts to register with one of our sites, our traffic rules will determine which order of partners
        we attempt to sign them up with.
      </p>
      <p>
        We may want to distribute users to different partners so we give them a "percentage" value, aka
        <strong>traffic distribution</strong>.
      </p>
      <p class="font-italic">
        For example, Horka and ROI Collective have 50% applied to them both here. This means that 50% of users will
        randomly be sent to Horka, and the other 50% sent to ROI Collective.
      </p>
      <p>
        Sometimes a user can be registered with the first partner we try, but this isn't always the case. If a
        registration fails, we attempt to register the user with another partner with the same priority.
        <span class="font-italic"
          >So, for example, if Horka failed we would attempt to register with ROI Collective.</span
        >
      </p>
      <traffic-partner-list
        :partners="dummyPartnerTraffic[0].partners"
        :priority="dummyPartnerTraffic[0].priority"
        disabled
        optional-label="Example"
        class="my-4"
      />
      <p>
        Traffic rules can assign <strong>priority</strong> values. Think of this as a stage in the registration process.
        If all partners in priority (stage) 1 fail, we will attempt to register with partners in priority (stage) 2.
      </p>
      <traffic-partner-list
        :partners="dummyPartnerTraffic[1].partners"
        :priority="dummyPartnerTraffic[1].priority"
        disabled
        optional-label="Example"
        class="my-4"
      />
      <p>
        Any partners remaining that do not have any priority or traffic distribution specified are pooled together and
        given random but equal chance of registration.
      </p>
      <traffic-partner-list
        :partners="dummyPartnerTraffic[2].partners"
        :priority="dummyPartnerTraffic[2].priority"
        disabled
        optional-label="Example"
        class="my-4"
      />
      <v-btn color="primary" class="mt-6" depressed @click="showHelpModal = false">Cool! Thanks, Louisbot!</v-btn>
    </generic-modal>

    <v-tooltip left>
      <template #activator="{ on }">
        <v-btn color="primary" fab fixed bottom right @click="showHelpModal = true" v-on="on">
          <v-icon>question_mark</v-icon>
        </v-btn>
      </template>
      <span>Traffic Flow Help</span>
    </v-tooltip>
  </page-loader>
</template>

<script>
// Services
import { offers, partners, registrations, sites } from '@/services/offers'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
// Helpers
import { mapGetters, mapMutations } from 'vuex'
import { merge } from 'lodash-es'
// Components
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SelectList from '@/components/forms/select-list'
import TrafficPartnerList from '@/views/traffic/components/traffic-partner-list'

export default {
  name: 'TrafficPreview',
  components: { GenericModal, PageLoader, SelectList, TrafficPartnerList },
  mixins: [QueryParamsHandler],
  data: () => ({
    isLoading: false,
    offers: [],
    defaultQuery: {
      countryCode: 'GB',
      language: 'en',
      offerType: 'crypto',
    },
    dummyPartnerTraffic: [
      {
        priority: 1,
        partners: [
          {
            id: 300,
            slug: 'horka',
            name: 'Horka',
            published: true,
            rule: { percentage: 50, priority: 1 },
          },
          {
            id: 189,
            slug: 'roi-collective',
            name: 'ROI Collective',
            published: true,
            rule: { percentage: 50, priority: 1 },
          },
        ],
      },
      {
        priority: 2,
        partners: [
          {
            id: 192,
            slug: 'megasource',
            name: 'Megasource',
            published: true,
            rule: { percentage: 100, priority: 2 },
          },
        ],
      },
      {
        priority: 999,
        partners: [
          {
            id: 203,
            slug: 'algo',
            name: 'Algo',
            rule: { percentage: 0, priority: 999 },
          },
          {
            id: 268,
            slug: 'investmarkets',
            name: 'InvestMarkets',
            rule: { percentage: 0, priority: 999 },
          },
          {
            id: 194,
            slug: 'mojo-media',
            name: 'Mojo Media',
            rule: { percentage: 0, priority: 999 },
          },
        ],
      },
    ],
    queryDefaults: ['offerType', 'countryCode', 'language'],
    queriesDefined: ['offerType', 'offer', 'countryCode', 'language', 'site'],
    queryPagination: false,
    querySearch: false,
    partners: [],
    showHelpModal: false,
    sites: [],
    trafficPartners: [],
  }),
  computed: {
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
    }),
    canPreview() {
      return !this.missingQueryParams.length
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    languages() {
      return this.getItemsForOption('languages')
    },
    missingQueryParams() {
      return this.queriesDefined.filter(query => !this.query.params[query])
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    mappedTrafficPartners() {
      return this.trafficPartners.map(({ partner: slug, rule }) => {
        return { ...this.partners.find(partner => partner.slug === slug), rule }
      })
    },
    orderedTrafficPartners() {
      const groupedPartnersByPriority = this.mappedTrafficPartners.reduce((order, originalPartner) => {
        // Change blocked rule types to use 999 priority to merge with 'remaining partners'
        const partner = merge({}, originalPartner, {
          rule: { priority: originalPartner.rule.type === 'block' ? 999 : originalPartner.rule.priority },
        })

        return !order[partner.rule.priority]
          ? Object.assign({}, order, { [partner.rule.priority]: [partner] })
          : Object.assign({}, order, { [partner.rule.priority]: [...order[partner.rule.priority], partner] })
      }, {})

      return Object.entries(groupedPartnersByPriority)
        .reduce((order, [priority, partners]) => {
          return [...order, { priority: Number(priority), partners }]
        }, [])
        .sort((a, b) => (a.priority < b.priority ? -1 : a.priority > b.priority ? 1 : 0))
    },
    siteTypes() {
      return this.getItemsForOption('siteTypes').filter(({ value }) => value)
    },
    mappedSites() {
      return this.sites.map(({ name, hostname }) => ({
        key: name || hostname,
        value: hostname,
      }))
    },
  },
  watch: {
    'query.params.offerType': {
      handler(newValue, oldValue) {
        // Reset 'offer' query when 'offerType' is changed.
        if (oldValue) this.$set(this.query.params, 'offer', undefined)
        // Only call getOffers if 'offerType' is defined.
        if (this.query.params.offerType) this.getOffers()
      },
      immediate: true,
    },
  },
  async created() {
    await this.getOffers()
    await this.getPartners()
    await this.getSites()

    this.setPageLoading(false)
    if (this.canPreview) this.getTrafficRulePartners()
  },
  methods: {
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    async getOffers() {
      if (!this.query.params.offerType) {
        this.$notify({
          type: 'error',
          text: 'A valid Offer Type must be selected!',
          duration: -1,
        })
        return
      }

      this.isLoading = true
      try {
        const { data } = await offers.getAllOffers({
          compact: true,
          filters: { type: this.query.params.offerType },
        })
        this.offers = data.map(({ name, slug }) => ({ key: name, value: slug }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch offers',
        })
      }
      this.isLoading = false
    },
    async getPartners() {
      this.isLoading = true
      try {
        const { data } = await partners.getAllPartners({ compact: true })
        this.partners = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch partners',
        })
      }
      this.isLoading = false
    },
    async getSites() {
      this.isLoading = true
      try {
        const { data } = await sites.getAllSites({ type: this.query.params.siteType })
        this.sites = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch sites',
        })
      }
      this.isLoading = false
    },
    async getTrafficRulePartners() {
      if (!this.canPreview) return

      this.isLoading = true
      try {
        const { offer, countryCode, language, site: hostname } = this.query.params

        const response = await registrations.previewRegistration({ offer, countryCode, hostname, language })

        if (Array.isArray(response)) {
          this.trafficPartners = response
        } else {
          this.trafficPartners = []
          this.$notify({
            type: 'error',
            text: `Unexpected response from preview service. Expected list of partners, recieved something else.`,
            duration: -1,
          })
        }
      } catch (e) {
        console.log(e)
        this.trafficPartners = []
        this.$notify({
          type: 'error',
          text: `Unable to fetch preview. \n${e}`,
        })
      }
      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getTrafficRulePartners()
    },
  },
}
</script>
